import { injectStrict } from '@/utils/injectStrict';
import {
  confirmationInjectionKeys,
  ConfirmationModalInjectionKeyType,
  type ConfirmationModalOptions,
} from './types';
import { ref } from 'vue';

interface UseConfirmationDialog<T, R> {
  confirmedAction: (data: T) => R | PromiseLike<R>; // actually PromiseLike<R> should not be needed but problems with infering
  confirmationOptions?: ConfirmationModalOptions;
}

// think about adding additional modals like `useDeleteActionConfirmationModal` with defaults, when repeating to often
export function useActionWithConfirmation<T, R>({
  confirmedAction,
  confirmationOptions: confirmationBaseOptions,
}: UseConfirmationDialog<T, R>) {
  const { openDialog } = injectStrict<ConfirmationModalInjectionKeyType>(
    confirmationInjectionKeys.action,
  );
  const loading = ref(false);

  const action = async ({
    param,
    confirmationOptions: confirmationActionOptions = {},
  }: {
    param: T;
    confirmationOptions?: ConfirmationModalOptions;
  }) => {
    const success = await new Promise<boolean>((resolve) => {
      openDialog(resolve, {
        ...confirmationBaseOptions,
        ...confirmationActionOptions,
      });
    });

    if (success) {
      loading.value = true;
      // confirmedAction can be synchronous or asynchronous, so we need to handle both situations
      const result = await confirmedAction(param);
      loading.value = false;
      return {
        confirmed: true as const,
        result: result,
      };
    } else {
      return { confirmed: false as const };
    }
  };

  return { action, loading };
}
